// src/feature-module/crm/power-dialer/PowerDialerLists.jsx
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import * as XLSX from "xlsx";
import api from "../../../core/axios/axiosInstance";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { fetchTags } from "../../../core/data/redux/slices/TagSlice";

const CARD_SHADOW = "0 1px 3px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.05)";
const MODAL_SHADOW = "0 20px 60px rgba(0,0,0,.18)";

function StatCard({ label, value, icon, iconBg, iconColor }) {
  return (
    <div className="col-6 col-md-3">
      <div className="card border-0 h-100" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
        <div className="card-body py-3 px-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <p
                className="mb-1 fw-semibold text-muted text-uppercase"
                style={{ fontSize: 11, letterSpacing: ".6px" }}
              >
                {label}
              </p>
              <h3 className="fw-bold mb-0" style={{ fontSize: 26 }}>
                {value}
              </h3>
            </div>
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{ width: 46, height: 46, background: iconBg }}
            >
              <i className={`ti ${icon} fs-4`} style={{ color: iconColor }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const sourceMeta = (src) =>
  src === "group"
    ? { label: "Groups", bg: "rgba(234,179,8,.14)", color: "#92400e" }
    : { label: "CSV / XLSX", bg: "rgba(59,130,246,.13)", color: "#1d4ed8" };

const statusMeta = (status) => {
  const map = {
    called: { label: "Called", bg: "rgba(16,185,129,.14)", color: "#065f46" },
    failed: { label: "Failed", bg: "rgba(239,68,68,.14)", color: "#991b1b" },
  };
  return map[status] || { label: "Pending", bg: "rgba(107,114,128,.12)", color: "#374151" };
};

function ConfirmModal({ show, title, message, confirmLabel = "Confirm", confirmColor = "#dc2626", confirmBg = "rgba(239,68,68,.12)", onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
          <div className="modal-body px-4 pt-4 pb-3">
            <h6 className="fw-bold mb-2" style={{ color: "#111827", fontSize: 16 }}>{title}</h6>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>{message}</p>
          </div>
          <div className="modal-footer border-0 px-4 pt-0 pb-4 gap-2">
            <button
              className="btn fw-semibold px-4"
              style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="btn fw-semibold px-4"
              style={{ background: confirmBg, color: confirmColor, borderRadius: 10, border: "none" }}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineBadge({ bg, color, label }) {
  return (
    <span
      className="fw-semibold"
      style={{
        background: bg,
        color,
        borderRadius: 6,
        padding: "3px 10px",
        fontSize: 12,
      }}
    >
      {label}
    </span>
  );
}

export default function PowerDialerLists() {
  const dispatch = useDispatch();
  const userProfile = useSelector((s) => s.profile);
  const tags = useSelector((s) => s.tags?.tags || []);
  const isAdmin = userProfile?.role === "companyAdmin";

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignListId, setAssignListId] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false); // fix: proper loading state
  const [selectedAgents, setSelectedAgents] = useState([]);

  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contactsListName, setContactsListName] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const [editList, setEditList] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [form, setForm] = useState({ name: "", source: "csv", file: null, group_ids: [] });
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState({ show: false, title: "", message: "", confirmLabel: "Confirm", confirmColor: "#dc2626", confirmBg: "rgba(239,68,68,.12)", onConfirm: null });

  const openConfirm = (title, message, onConfirm, opts = {}) =>
    setConfirm({ show: true, title, message, onConfirm, confirmLabel: opts.confirmLabel || "Confirm", confirmColor: opts.confirmColor || "#dc2626", confirmBg: opts.confirmBg || "rgba(239,68,68,.12)" });
  const closeConfirm = () => setConfirm((p) => ({ ...p, show: false }));

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/power-dialer/lists");
      setLists(res.data.data || []);
    } catch {
      dispatch(showToast({ message: "Failed to load lists", variant: "danger" }));
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    setAgentsLoading(true);
    try {
      const res = await api.get("/getUser/getAgents");
      setAgents(
        (res.data.users || []).map((u) => ({
          value: u._id,
          label: `${u.firstname} ${u.lastname}`,
        }))
      );
    } catch {
    } finally {
      setAgentsLoading(false);
    }
  };

  const openContactsModal = async (list) => {
    setContactsListName(list.name);
    setContacts([]);
    setContactsLoading(true);
    setShowContactsModal(true);
    try {
      const res = await api.get(`/api/power-dialer/lists/${list._id}/contacts`);
      setContacts(res.data.data || []);
    } catch {
      dispatch(showToast({ message: "Failed to load contacts", variant: "danger" }));
      setShowContactsModal(false);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
    fetchAgents();
    dispatch(fetchTags());
  }, []);

  const handleFileChange = (e) => {
    setForm((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const downloadSample = () => {
    const rows = [
      { firstname: "John",  lastname: "Smith",  phonenumbers: "971501234567", emailaddresses: "john@example.com",  notes: "Interested in product A" },
      { firstname: "Sara",  lastname: "Ahmed",  phonenumbers: "971509876543", emailaddresses: "sara@example.com",  notes: "" },
      { firstname: "Ali",   lastname: "Hassan", phonenumbers: "971521234567", emailaddresses: "ali@example.com",   notes: "Call back Friday" },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 24 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "power_dialer_sample.xlsx");
  };

  const parseXlsx = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    });

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      dispatch(showToast({ message: "Enter list name", variant: "danger" }));
      return;
    }
    setSubmitting(true);
    try {
      let payload = { name: form.name, source: form.source };
      if (form.source === "csv") {
        if (!form.file) {
          dispatch(showToast({ message: "Select a file", variant: "danger" }));
          setSubmitting(false);
          return;
        }
        const rows = await parseXlsx(form.file);
        payload.contacts = rows
          .map((r) => {
            const firstname = r.firstname || r["First Name"] || r.FirstName || "";
            const lastname  = r.lastname  || r["Last Name"]  || r.LastName  || "";
            const fullName  = [firstname, lastname].filter(Boolean).join(" ").trim();
            return {
              firstname,
              lastname,
              name:  fullName || r.Name || r.name || "",
              phone: String(r.phonenumbers || r.Number || r.number || r.Phone || r.phone || ""),
              email: String(r.emailaddresses || r.Email || r.email || r.EmailAddress || ""),
              notes: String(r.notes || r.Notes || ""),
            };
          })
          .filter((c) => c.phone);
      } else {
        payload.group_ids = form.group_ids;
      }
      await api.post("/api/power-dialer/lists", payload);
      dispatch(showToast({ message: "List created", variant: "success" }));
      setShowModal(false);
      setForm({ name: "", source: "csv", file: null, group_ids: [] });
      fetchLists();
    } catch (err) {
      dispatch(
        showToast({
          message: err.response?.data?.message || "Failed to create list",
          variant: "danger",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) =>
    openConfirm(
      "Delete List",
      "This will permanently delete the list and all its contacts. This cannot be undone.",
      async () => {
        closeConfirm();
        try {
          await api.delete(`/api/power-dialer/lists/${id}`);
          dispatch(showToast({ message: "List deleted", variant: "success" }));
          fetchLists();
        } catch {
          dispatch(showToast({ message: "Failed to delete", variant: "danger" }));
        }
      }
    );

  const handleReset = (id) =>
    openConfirm(
      "Reset List",
      "All contacts in this list will be reset back to pending. Previous call data will be cleared.",
      async () => {
        closeConfirm();
        try {
          await api.put(`/api/power-dialer/lists/${id}/reset`);
          dispatch(showToast({ message: "List reset to pending", variant: "success" }));
          fetchLists();
        } catch {
          dispatch(showToast({ message: "Reset failed", variant: "danger" }));
        }
      },
      { confirmLabel: "Reset", confirmColor: "#d97706", confirmBg: "rgba(245,158,11,.12)" }
    );

  const handleEditSave = async () => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      await api.put(`/api/power-dialer/lists/${editList._id}`, { name: editName.trim() });
      dispatch(showToast({ message: "List name updated", variant: "success" }));
      setEditList(null);
      fetchLists();
    } catch {
      dispatch(showToast({ message: "Failed to update list name", variant: "danger" }));
    } finally {
      setEditSaving(false);
    }
  };

  const handleAssignSave = async () => {
    try {
      await api.post(`/api/power-dialer/lists/${assignListId}/assign`, {
        agent_ids: selectedAgents.map((a) => a.value),
      });
      dispatch(showToast({ message: "Assignment saved", variant: "success" }));
      setShowAssignModal(false);
      fetchLists();
    } catch {
      dispatch(showToast({ message: "Assignment failed", variant: "danger" }));
    }
  };

  const groupOptions = (tags || []).map((t) => ({
    value: t.tag_id,
    label: `${t.emoji || ""} ${t.tag}`,
  }));

  const totalContacts = lists.reduce((sum, l) => sum + (l.total_contacts || 0), 0);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="mb-4">
          <h4 className="fw-bold mb-1" style={{ color: "#111827" }}>
            Contact Lists
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Manage contact lists for Power Dialer campaigns
          </p>
        </div>

        {/* Stats + New List button */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="row g-3 flex-fill">
            <StatCard
              label="Total Lists"
              value={lists.length}
              icon="ti-list"
              iconBg="rgba(99,102,241,.12)"
              iconColor="#4f46e5"
            />
            <StatCard
              label="Total Contacts"
              value={totalContacts.toLocaleString()}
              icon="ti-users"
              iconBg="rgba(16,185,129,.12)"
              iconColor="#059669"
            />
          </div>
          {isAdmin && (
            <button
              className="btn btn-primary d-flex align-items-center gap-2 fw-semibold px-4 flex-shrink-0"
              style={{ borderRadius: 10, height: 40 }}
              onClick={() => setShowModal(true)}
            >
              <i className="ti ti-plus" style={{ fontSize: 16 }} />
              New List
            </button>
          )}
        </div>

        {/* Table */}
        <div className="card border-0" style={{ borderRadius: 14, boxShadow: CARD_SHADOW }}>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : lists.length === 0 ? (
            <div className="py-5 text-center">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3 mb-3"
                style={{ width: 56, height: 56, background: "rgba(99,102,241,.1)" }}
              >
                <i className="ti ti-playlist-x fs-2 text-primary" />
              </div>
              <p className="fw-semibold mb-1" style={{ color: "#111827" }}>
                No lists yet
              </p>
              <p className="text-muted small mb-3">Upload your first contact list to get started</p>
              {isAdmin && (
                <button
                  className="btn btn-primary btn-sm fw-semibold px-4"
                  style={{ borderRadius: 8 }}
                  onClick={() => setShowModal(true)}
                >
                  <i className="ti ti-plus me-1" /> Upload List
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0" style={{ borderCollapse: "separate" }}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    {["List Name", "Source", "Contacts", "Assigned To", "Created", "Actions"].map(
                      (h, i) => (
                        <th
                          key={h}
                          className={`fw-semibold text-muted text-uppercase py-3 ${i === 0 ? "ps-4" : ""} ${i === 5 ? "pe-4" : ""}`}
                          style={{ fontSize: 11, letterSpacing: ".6px", borderBottom: "1px solid #f0f1f3" }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lists.map((l, idx) => {
                    const sm = sourceMeta(l.source);
                    return (
                      <tr
                        key={l._id}
                        style={{
                          borderBottom: idx < lists.length - 1 ? "1px solid #f4f5f7" : "none",
                        }}
                      >
                        <td className="ps-4 py-3">
                          <span className="fw-semibold" style={{ color: "#111827", fontSize: 14 }}>
                            {l.name}
                          </span>
                        </td>
                        <td>
                          <InlineBadge bg={sm.bg} color={sm.color} label={sm.label} />
                        </td>
                        <td>
                          <span className="fw-bold" style={{ color: "#111827" }}>
                            {(l.total_contacts || 0).toLocaleString()}
                          </span>
                        </td>
                        <td>
                          {(l.assigned_to || []).length === 0 ? (
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              All agents
                            </span>
                          ) : (
                            <div className="d-flex flex-wrap gap-1">
                              {(l.assigned_to || []).slice(0, 2).map((a) => (
                                <span
                                  key={a._id}
                                  style={{
                                    background: "rgba(99,102,241,.1)",
                                    color: "#4f46e5",
                                    borderRadius: 6,
                                    padding: "2px 9px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}
                                >
                                  {a.firstname} {a.lastname}
                                </span>
                              ))}
                              {(l.assigned_to || []).length > 2 && (
                                <span
                                  style={{
                                    background: "#f3f4f6",
                                    color: "#6b7280",
                                    borderRadius: 6,
                                    padding: "2px 9px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}
                                >
                                  +{(l.assigned_to || []).length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ color: "#6b7280", fontSize: 13 }}>
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                        <td className="pe-4">
                          <div className="dropdown">
                            <button
                              className="btn btn-sm d-flex align-items-center justify-content-center"
                              style={{
                                background: "rgba(107,114,128,.1)",
                                color: "#374151",
                                borderRadius: 8,
                                border: "none",
                                width: 32,
                                height: 32,
                                padding: 0,
                              }}
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i className="ti ti-dots-vertical" style={{ fontSize: 16 }} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ minWidth: 170, borderRadius: 10, border: "1px solid #f0f1f3", padding: "6px 0" }}>
                              <li>
                                <button
                                  className="dropdown-item d-flex align-items-center gap-2"
                                  style={{ fontSize: 13, color: "#0f766e", padding: "7px 16px" }}
                                  onClick={() => openContactsModal(l)}
                                >
                                  <i className="ti ti-eye" style={{ fontSize: 15 }} />
                                  View Contacts
                                </button>
                              </li>
                              {isAdmin && (
                                <>
                                  <li>
                                    <button
                                      className="dropdown-item d-flex align-items-center gap-2"
                                      style={{ fontSize: 13, color: "#4f46e5", padding: "7px 16px" }}
                                      onClick={() => { setEditList(l); setEditName(l.name); }}
                                    >
                                      <i className="ti ti-pencil" style={{ fontSize: 15 }} />
                                      Edit Name
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className="dropdown-item d-flex align-items-center gap-2"
                                      style={{ fontSize: 13, color: "#4f46e5", padding: "7px 16px" }}
                                      onClick={() => {
                                        setAssignListId(l._id);
                                        setSelectedAgents(
                                          (l.assigned_to || []).map((a) => ({
                                            value: a._id,
                                            label: `${a.firstname} ${a.lastname}`,
                                          }))
                                        );
                                        setShowAssignModal(true);
                                      }}
                                    >
                                      <i className="ti ti-user-plus" style={{ fontSize: 15 }} />
                                      Assign to Agents
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" style={{ margin: "4px 0" }} /></li>
                                  <li>
                                    <button
                                      className="dropdown-item d-flex align-items-center gap-2"
                                      style={{ fontSize: 13, color: "#dc2626", padding: "7px 16px" }}
                                      onClick={() => handleDelete(l._id)}
                                    >
                                      <i className="ti ti-trash" style={{ fontSize: 15 }} />
                                      Delete
                                    </button>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Upload List Modal ── */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: MODAL_SHADOW }}>
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "#111827" }}>
                    Upload Contact List
                  </h5>
                  <p className="text-muted small mb-0 mt-1">
                    Import from a file or from existing groups
                  </p>
                </div>
                <button
                  className="btn-close"
                  style={{ position: "absolute", right: 20, top: 20 }}
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body px-4 py-3">
                {/* Source selector */}
                <div className="d-flex gap-3 mb-4">
                  {[
                    { value: "csv", label: "CSV / XLSX", icon: "ti-file-spreadsheet", desc: "Upload a file" },
                    { value: "group", label: "From Groups", icon: "ti-users-group", desc: "Use existing groups" },
                  ].map((opt) => {
                    const active = form.source === opt.value;
                    return (
                      <div
                        key={opt.value}
                        className="flex-fill p-3"
                        style={{
                          border: `2px solid ${active ? "#4f46e5" : "#e5e7eb"}`,
                          borderRadius: 12,
                          cursor: "pointer",
                          background: active ? "rgba(99,102,241,.06)" : "#fff",
                          transition: "all .15s",
                        }}
                        onClick={() => setForm((p) => ({ ...p, source: opt.value }))}
                      >
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i
                            className={`ti ${opt.icon}`}
                            style={{ fontSize: 18, color: active ? "#4f46e5" : "#9ca3af" }}
                          />
                          <span
                            className="fw-semibold"
                            style={{ fontSize: 13, color: active ? "#4f46e5" : "#374151" }}
                          >
                            {opt.label}
                          </span>
                        </div>
                        <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                          {opt.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#374151" }}>
                    List Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    style={{ borderRadius: 10, fontSize: 14 }}
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Dubai Leads June 2025"
                  />
                </div>

                {form.source === "csv" ? (
                  <div className="mb-1">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label fw-semibold mb-0" style={{ fontSize: 13, color: "#374151" }}>
                        File
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm fw-semibold d-flex align-items-center gap-1"
                        style={{
                          background: "rgba(16,185,129,.1)",
                          color: "#059669",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 12,
                          padding: "4px 12px",
                        }}
                        onClick={downloadSample}
                      >
                        <i className="ti ti-download" style={{ fontSize: 13 }} />
                        Download Sample
                      </button>
                    </div>
                    <input
                      className="form-control"
                      style={{ borderRadius: 10, fontSize: 14 }}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                    />
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
                      Required columns:{" "}
                      <strong className="text-dark">Name</strong>,{" "}
                      <strong className="text-dark">Phone</strong> (with country code, no spaces or +)
                    </p>
                  </div>
                ) : (
                  <div className="mb-1">
                    <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#374151" }}>
                      Select Groups
                    </label>
                    <Select
                      isMulti
                      options={groupOptions}
                      value={groupOptions.filter((g) =>
                        form.group_ids.includes(g.value?.toString())
                      )}
                      onChange={(opts) =>
                        setForm((p) => ({
                          ...p,
                          group_ids: opts.map((o) => o.value?.toString()),
                        }))
                      }
                      placeholder="Select groups..."
                      classNamePrefix="react-select"
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pt-2 pb-4">
                <button
                  className="btn fw-semibold px-4"
                  style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary fw-semibold px-4"
                  style={{ borderRadius: 10 }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload me-2" />
                      Upload List
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit List Name Modal ── */}
      {editList && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.45)" }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <h6 className="fw-bold mb-0" style={{ color: "#111827", fontSize: 16 }}>Edit List Name</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditList(null)}
                />
              </div>
              <div className="modal-body px-4 py-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#374151" }}>
                  List Name
                </label>
                <input
                  className="form-control"
                  style={{ borderRadius: 8, fontSize: 14 }}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEditSave()}
                  autoFocus
                />
              </div>
              <div className="modal-footer border-0 px-4 pb-4 pt-0 gap-2">
                <button
                  className="btn btn-light fw-semibold px-4"
                  style={{ borderRadius: 8, fontSize: 14 }}
                  onClick={() => setEditList(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary fw-semibold px-4"
                  style={{ borderRadius: 8, fontSize: 14 }}
                  onClick={handleEditSave}
                  disabled={editSaving || !editName.trim()}
                >
                  {editSaving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ── */}
      {showAssignModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: MODAL_SHADOW }}>
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "#111827" }}>
                    Assign to Agents
                  </h5>
                  <p className="text-muted small mb-0 mt-1">
                    Only selected agents will have access to this list
                  </p>
                </div>
                <button
                  className="btn-close"
                  style={{ position: "absolute", right: 20, top: 20 }}
                  onClick={() => setShowAssignModal(false)}
                />
              </div>
              <div className="modal-body px-4 py-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#374151" }}>
                  Select Agents
                </label>
                <Select
                  isMulti
                  options={agents}
                  value={selectedAgents}
                  onChange={setSelectedAgents}
                  isLoading={agentsLoading}
                  placeholder={agentsLoading ? "Loading agents…" : "Select agents…"}
                  classNamePrefix="react-select"
                />
              </div>
              <div className="modal-footer border-0 px-4 pt-2 pb-4">
                <button
                  className="btn fw-semibold px-4"
                  style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary fw-semibold px-4"
                  style={{ borderRadius: 10 }}
                  onClick={handleAssignSave}
                >
                  <i className="ti ti-check me-2" />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View Contacts Modal ── */}
      {showContactsModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,.5)", backdropFilter: "blur(2px)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0" style={{ borderRadius: 16, boxShadow: MODAL_SHADOW }}>
              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "#111827" }}>
                    {contactsListName}
                  </h5>
                  {!contactsLoading && (
                    <p className="text-muted small mb-0 mt-1">
                      {contacts.length.toLocaleString()} contacts
                    </p>
                  )}
                </div>
                <button
                  className="btn-close"
                  style={{ position: "absolute", right: 20, top: 20 }}
                  onClick={() => setShowContactsModal(false)}
                />
              </div>
              <div className="modal-body px-4 py-3">
                {contactsLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-5 text-muted">No contacts in this list.</div>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: "auto", borderRadius: 10, border: "1px solid #f0f1f3" }}>
                    <table className="table align-middle mb-0">
                      <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
                        <tr>
                          {["#", "Name", "Phone"].map((h) => (
                            <th
                              key={h}
                              className="fw-semibold text-muted text-uppercase py-3 ps-3"
                              style={{ fontSize: 11, letterSpacing: ".6px", border: "none", borderBottom: "1px solid #f0f1f3" }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c, i) => {
                          return (
                            <tr key={c._id || i} style={{ borderBottom: "1px solid #f9fafb" }}>
                              <td className="ps-3 text-muted" style={{ fontSize: 13 }}>
                                {i + 1}
                              </td>
                              <td className="ps-3" style={{ fontSize: 14, fontWeight: 500 }}>
                                {c.name || "—"}
                              </td>
                              <td className="ps-3" style={{ fontSize: 14, color: "#374151" }}>
                                {c.phone}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pt-2 pb-4">
                <button
                  className="btn fw-semibold px-4"
                  style={{ background: "#f3f4f6", color: "#374151", borderRadius: 10, border: "none" }}
                  onClick={() => setShowContactsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirm.show}
        title={confirm.title}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        confirmColor={confirm.confirmColor}
        confirmBg={confirm.confirmBg}
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}
